'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { Format, getFileExtension, FORMAT_MIME_TYPES, getFormatDisplayName } from '../lib/formats';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, FileUploadArea, Button, Progress } from './ui';
import { Files, Download, X, CheckCircle, AlertCircle, Loader2, FileText, Table } from 'lucide-react';
import { downloadFromUrl } from '../lib/export-utils';
import { trackAction } from '../lib/tracking';
import { useRouter } from 'next/navigation';
import { preprocessFile } from '../lib/preprocess';
import { useNavigateToViewer } from '../hooks/use-navigate-to-viewer';

// File conversion interface
interface FileToConvert {
  id: string;
  file: File;
  status: 'setting-up' | 'uploading' | 'converting' | 'done' | 'error';
  error?: string;
  downloadUrl?: string;
  format?: Format;
  autoDownloadAttempted?: boolean;
  uploadProgress?: number;
}

interface FileConverterProps {
  /**
   * The initial source format to convert from
   */
  initialSourceFormat?: Format;
  
  /**
   * The initial destination format to convert to
   */
  initialDestinationFormat?: Format;

  /**
   * Whether to lock the source format (prevent changing it)
   * Used when on a specific conversion page like /parquet/to/csv
   */
  lockSourceFormat?: boolean;
}

export function FileConverter({ 
  initialSourceFormat = Format.CSV, 
  initialDestinationFormat = Format.JSON,
  lockSourceFormat = false
}: FileConverterProps) {
  const [sourceFormat, setSourceFormat] = useState<Format>(initialSourceFormat);
  const [destinationFormat, setDestinationFormat] = useState<Format>(initialDestinationFormat);
  const [files, setFiles] = useState<FileToConvert[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const uploadProgressRef = useRef<Record<string, number>>({});
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Use the useNavigateToViewer hook
  const { viewFile: navigateToViewer } = useNavigateToViewer();

  // Handle source format change (only if not locked)
  const handleSourceFormatChange = useCallback((value: string) => {
    if (!lockSourceFormat) {
      setSourceFormat(value as Format);
    }
  }, [lockSourceFormat]);

  // Handle file upload
  const handleFileUpload = useCallback((uploadedFiles: FileList | null) => {
    if (!uploadedFiles) return;

    const newFiles = Array.from(uploadedFiles).map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      status: 'setting-up' as const,
      format: sourceFormat
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, [sourceFormat]);

  // Remove file from the list
  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(file => file.id !== id));
  }, []);

  // Convert files
  const convertFiles = useCallback(async () => {
    // Only convert files that are in setting-up state AND match the current source format
    const pendingFiles = files.filter(f => f.status === 'setting-up' && f.format === sourceFormat);
    if (pendingFiles.length === 0) return;

    // Track conversion started event
    trackAction('conversion started', {
      formatFrom: sourceFormat,
      formatTo: destinationFormat,
      fileCount: pendingFiles.length,
      totalSizeMB: Number((pendingFiles.reduce((sum, file) => sum + file.file.size, 0) / (1024 * 1024)).toFixed(2))
    });

    // Process each file
    const results = await Promise.all(
      pendingFiles.map(async (fileToConvert) => {
        try {
          // Get the original filename without extension
          const originalFilename = fileToConvert.file.name.split('.')[0];

          // Already in 'setting-up' status while getting URLs
          const urlsResponse = await fetch('/api/convert/urls', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sourceFormat,
              destinationFormat,
              filename: originalFilename
            }),
          });

          if (!urlsResponse.ok) {
            throw new Error(`Failed to get upload URLs: ${await urlsResponse.text()}`);
          }

          const urls = await urlsResponse.json();

          // Preprocess the file if needed (e.g. JSON to JSONL)
          const processedFile = await preprocessFile({
            file: fileToConvert.file,
            format: sourceFormat
          });
          const fileContent = new Blob([processedFile], { type: urls.source.contentType });
          
          setFiles(prev => prev.map(f => 
            f.id === fileToConvert.id 
              ? { ...f, status: 'uploading' as const } 
              : f
          ));
          setUploadProgress(prev => ({ ...prev, [fileToConvert.id]: 0 }));

          // Upload using Supabase with progress tracking
          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            
            xhr.upload.addEventListener('progress', (event) => {
              if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                // Update ref immediately
                uploadProgressRef.current[fileToConvert.id] = progress;
                // Debounce the state update
                setUploadProgress(prev => {
                  // Only update if the progress has increased
                  if (progress > (prev[fileToConvert.id] || 0)) {
                    return { ...prev, [fileToConvert.id]: progress };
                  }
                  return prev;
                });
              }
            });

            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                setUploadProgress(prev => ({ ...prev, [fileToConvert.id]: 100 }));
                resolve(null);
              } else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
              }
            });

            xhr.addEventListener('error', () => {
              reject(new Error('Upload failed'));
            });

            xhr.open('PUT', urls.source.uploadUrl);
            xhr.setRequestHeader('Content-Type', urls.source.contentType);
            xhr.send(fileContent);
          });

          // Clean up progress tracking for this file
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileToConvert.id];
            return newProgress;
          });

          // Step 3: Update to converting status and trigger conversion
          setFiles(prev => prev.map(f => 
            f.id === fileToConvert.id 
              ? { ...f, status: 'converting' as const } 
              : f
          ));

          const convertResponse = await fetch('/api/convert', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sourceUrl: urls.source.filePath,
              sourceFormat,
              destinationFormat,
              filename: originalFilename,
            }),
          });

          if (!convertResponse.ok) {
            const errorText = await convertResponse.text();
            throw new Error(`Conversion failed: ${errorText}`);
          }

          const { url: downloadUrl } = await convertResponse.json();

          // Step 4: Update to done status
          return {
            ...fileToConvert,
            status: 'done' as const,
            downloadUrl,
            autoDownloadAttempted: false
          };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          let userFriendlyMessage = errorMessage;
          
          // Check for the specific ClickHouse "Bad URI syntax" error
          if (
            (errorMessage.includes("Bad URI syntax") || 
             errorMessage.includes("Poco::Exception") || 
             errorMessage.includes("URI contains invalid characters")) && 
            sourceFormat === Format.CSV && 
            destinationFormat === Format.PARQUET
          ) {
            // Check if the filename has spaces or special characters
            const originalFilename = fileToConvert.file.name;
            if (/[^a-zA-Z0-9.-]/.test(originalFilename)) {
              userFriendlyMessage = "Your file name contains spaces or special characters. We've automatically sanitized it for compatibility with Parquet format. The converted file will use a simplified name.";
            } else {
              userFriendlyMessage = "There was an issue converting to Parquet format. This might be due to special characters in your data. Try converting to JSON or XLSX instead.";
            }
          }
          
          console.error("Conversion error:", errorMessage);
          
          return {
            ...fileToConvert,
            status: 'error' as const,
            error: userFriendlyMessage,
          };
        }
      })
    );

    // Update files with results
    setFiles(prev => {
      const newFiles = [...prev];
      
      results.forEach(result => {
        const index = newFiles.findIndex(f => f.id === result.id);
        if (index !== -1) {
          newFiles[index] = result;
        }
      });
      
      return newFiles;
    });

    // Track conversion completed event
    const successCount = results.filter(result => result.status === 'done').length;
    const errorCount = results.filter(result => result.status === 'error').length;
    
    trackAction('conversion completed', {
      formatFrom: sourceFormat,
      formatTo: destinationFormat,
      fileCount: pendingFiles.length,
      successCount,
      errorCount,
      successRate: pendingFiles.length > 0 ? (successCount / pendingFiles.length) * 100 : 0,
      totalSizeMB: Number((pendingFiles.reduce((sum, file) => sum + file.file.size, 0) / (1024 * 1024)).toFixed(2)),
      successSizeMB: Number((results
        .filter(result => result.status === 'done')
        .reduce((sum, file) => sum + file.file.size, 0) / (1024 * 1024)).toFixed(2))
    });
  }, [files, sourceFormat, destinationFormat]);

  // Effect to automatically start conversion when new files are added
  useEffect(() => {
    const pendingFiles = files.filter(f => f.status === 'setting-up');
    if (pendingFiles.length > 0) {
      convertFiles();
    }
  }, [files, convertFiles]);

  // Handle file download
  const handleDownload = useCallback((file: FileToConvert) => {
    if (file.downloadUrl) {
      // Use the original filename for the download
      const originalFilename = file.file.name.split('.')[0];
      downloadFromUrl(file.downloadUrl, `${originalFilename}.${destinationFormat}`);
    }
  }, [destinationFormat]);

  // Auto-download files when conversion is complete
  useEffect(() => {
    const newlyCompletedFiles = files.filter(file => 
      file.status === 'done' && 
      file.downloadUrl && 
      !file.autoDownloadAttempted
    );
    
    if (newlyCompletedFiles.length > 0) {
      // Mark files as having attempted auto-download
      setFiles(prev => prev.map(file => 
        newlyCompletedFiles.some(f => f.id === file.id)
          ? { ...file, autoDownloadAttempted: true }
          : file
      ));
      
      // Download all newly completed files
      newlyCompletedFiles.forEach(file => {
        handleDownload(file);
      });
    }
  }, [files, handleDownload]);

  // Handle viewing a converted file
  const handleViewFile = useCallback(async (file: FileToConvert) => {
    if (!file.downloadUrl) return;

    try {
      // Fetch the converted file
      const response = await fetch(file.downloadUrl);
      if (!response.ok) throw new Error('Failed to fetch converted file');
      
      // Create a File object from the response
      const blob = await response.blob();
      const convertedFile = new File([blob], `${file.file.name.split('.')[0]}.${destinationFormat}`, {
        type: FORMAT_MIME_TYPES[destinationFormat]
      });

      // Use the viewFile function from useNavigateToViewer hook
      await navigateToViewer({ file: convertedFile, source: 'conversion' });
    } catch (error) {
      console.error('Error viewing file:', error);
      // You might want to show an error message to the user here
    }
  }, [destinationFormat, navigateToViewer]);

  // Clear all files
  const clearAll = useCallback(() => {
    setFiles([]);
  }, []);

  return (
    <div>
      {/* Format Selection */}
      <div className="mb-8 flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-4">
        <div className="w-full md:w-1/3">
          <label htmlFor="source-format-select" className="block text-sm font-medium text-foreground mb-1">
            Convert from
          </label>
          <Select
            value={sourceFormat}
            onValueChange={handleSourceFormatChange}
            disabled={lockSourceFormat}
          >
            <SelectTrigger id="source-format-select" className="w-full">
              <SelectValue placeholder="Select source format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={Format.CSV}>CSV</SelectItem>
              <SelectItem value={Format.TSV}>TSV</SelectItem>
              <SelectItem value={Format.JSON}>JSON</SelectItem>
              <SelectItem value={Format.PARQUET}>Parquet</SelectItem>
              <SelectItem value={Format.XLSX}>Excel (XLSX)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="w-full md:w-1/3">
          <label htmlFor="destination-format-select" className="block text-sm font-medium text-foreground mb-1">
            Convert to
          </label>
          <Select
            value={destinationFormat}
            onValueChange={(value) => setDestinationFormat(value as Format)}
          >
            <SelectTrigger id="destination-format-select" className="w-full">
              <SelectValue placeholder="Select destination format" />
            </SelectTrigger>
            <SelectContent>
              {Object.values(Format).map(format => (
                // Skip the source format in the destination dropdown when source is locked
                (!lockSourceFormat || format !== sourceFormat) && (
                  <SelectItem key={format} value={format}>
                    {getFormatDisplayName(format)}
                  </SelectItem>
                )
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* File List with Upload Button */}
      <div className="mb-8">
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-card px-4 py-3 border-b flex justify-between items-center">
            <h3 className="text-sm font-medium text-foreground">Files {files.length > 0 ? `(${files.length})` : ""}</h3>
            <div className="flex space-x-2">
              <Button 
                variant={files.length > 0 ? "ghost" : "outline"}
                size="sm" 
                className={files.length > 0 
                  ? "bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary transition-colors font-medium"
                  : ""
                }
                onClick={() => {
                  // Create a file input element and trigger it
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.multiple = true;
                  input.accept = FORMAT_MIME_TYPES[sourceFormat];
                  input.onchange = (e) => handleFileUpload((e.target as HTMLInputElement).files);
                  input.click();
                }}
              >
                <Files className={`w-4 h-4 mr-2 ${files.length > 0 ? "text-primary" : ""}`} />
                {files.length > 0 
                  ? "Upload more files" 
                  : `Upload ${getFormatDisplayName(sourceFormat).toLowerCase()} files`
                }
              </Button>
              {files.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearAll}>
                  Clear all
                </Button>
              )}
            </div>
          </div>
          {files.length === 0 ? (
            <div className="p-8">
              <FileUploadArea
                formatName={getFormatDisplayName(sourceFormat)}
                acceptedMimeType={FORMAT_MIME_TYPES[sourceFormat]}
                multiple={true}
                onFilesSelected={handleFileUpload}
                buttonText={`Select ${getFormatDisplayName(sourceFormat).toLowerCase()} files`}
                icon={<Files className="w-8 h-8" />}
                buttonIcon={<Files className="w-4 h-4 mr-2" />}
              />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {files.map(file => (
                <li key={file.id} className="px-4 py-3">
                  <div className="flex justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-medium text-foreground truncate">{file.file.name}</span>
                      
                      {/* Status badges */}
                      {file.status === 'setting-up' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground flex-shrink-0">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Setting up
                        </span>
                      )}
                      {file.status === 'uploading' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary flex-shrink-0">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Uploading
                        </span>
                      )}
                      {file.status === 'converting' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary flex-shrink-0">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Converting
                        </span>
                      )}
                      {file.status === 'done' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-supernova-green/10 text-supernova-green flex-shrink-0">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Done
                        </span>
                      )}
                      {file.status === 'error' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-supernova-red/10 text-supernova-red flex-shrink-0">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Error
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {file.status === 'done' && file.downloadUrl && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownload(file)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewFile(file)}
                          >
                            <Table className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeFile(file.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Progress bar section - now below the main content */}
                  <div className="h-6 flex items-center">
                    {file.status === 'uploading' && (
                      <div className="w-full">
                        <div className="flex justify-between items-center mb-1">
                          <span className="inline-flex items-center text-xs font-medium text-primary">
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Uploading
                          </span>
                          <span className="text-xs font-medium text-primary">{uploadProgress[file.id] ?? 0}%</span>
                        </div>
                        <Progress 
                          value={uploadProgress[file.id] ?? 0} 
                          className="h-1.5 bg-primary/10" 
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Error message */}
                  {file.error && (
                    <div className="text-xs text-supernova-red mt-2">
                      {file.error.split('\n').map((line, i) => (
                        <div key={i} className={i > 0 ? "ml-2" : ""}>{line}</div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
} 